import { useState, useEffect } from "react";
import { ExpandedContent, Separator, TableContainer } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import LogsVazio from "../../Compartilhado/LogsVazio";
import { getStatusLabelDemanda } from "../../../../../../../utility/statusDemanda";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

interface CampoAlterado {
    campo: string;
    antes: any;
    depois: any;
}

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const formatarSimNao = (valor: any): string => {
    if (valor === null || valor === undefined || valor === '') return '—';
    if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
    const valorStr = String(valor).toLowerCase();
    if (valorStr === 'sim' || valorStr === 'yes' || valorStr === 'true' || valorStr === '1') return 'Sim';
    if (valorStr === 'nao' || valorStr === 'no' || valorStr === 'false' || valorStr === '0') return 'Não';
    return formatValue(valor);
};

const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
        'status': 'Status',
        'atrasada': 'Atrasada',
        'pendente_de_orientacao': 'Pendente de Orientação',
        'status_pendencias': 'Status de Pendências'
    };
    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const compararDados = (dadosAntes: any, dadosDepois: any): CampoAlterado[] => {
    const campos: CampoAlterado[] = [];

    if (dadosDepois?.status && dadosAntes?.status !== dadosDepois.status) {
        campos.push({
            campo: 'status',
            antes: getStatusLabelDemanda(dadosAntes?.status || 'Em Conferência'),
            depois: getStatusLabelDemanda(dadosDepois.status)
        });
    }

    if (dadosDepois?.atrasada !== undefined && dadosAntes?.atrasada !== dadosDepois.atrasada) {
        campos.push({
            campo: 'atrasada',
            antes: formatarSimNao(dadosAntes?.atrasada),
            depois: formatarSimNao(dadosDepois.atrasada)
        });
    }

    if (dadosDepois?.pendente_de_orientacao !== undefined && dadosAntes?.pendente_de_orientacao !== dadosDepois.pendente_de_orientacao) {
        campos.push({
            campo: 'pendente_de_orientacao',
            antes: formatarSimNao(dadosAntes?.pendente_de_orientacao),
            depois: formatarSimNao(dadosDepois.pendente_de_orientacao)
        });
    }

    if (dadosDepois?.status_pendencias !== undefined && dadosAntes?.status_pendencias !== dadosDepois.status_pendencias) {
        campos.push({
            campo: 'status_pendencias',
            antes: formatValue(dadosAntes?.status_pendencias),
            depois: formatValue(dadosDepois.status_pendencias)
        });
    }

    return campos;
};

export default function ExpandedContentDemandaFinalizada({ 
    item, 
    onNavigateToFile 
}: ExpandedContentProps) {
    const [camposAlterados, setCamposAlterados] = useState<CampoAlterado[]>([]);
    const [loading, setLoading] = useState(true);
    const [justificativa, setJustificativa] = useState<string | null>(null);

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response.data.status === 'success') {
                    const logs = response.data.result || [];
                    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');

                    if (logProposta) {
                        let dadosAntes = logProposta.dados_antes;
                        let dadosDepois = logProposta.dados_depois;

                        if (typeof dadosAntes === 'string') {
                            try {
                                dadosAntes = JSON.parse(dadosAntes);
                            } catch (e) {
                                dadosAntes = null;
                            }
                        }

                        if (typeof dadosDepois === 'string') {
                            try {
                                dadosDepois = JSON.parse(dadosDepois);
                            } catch (e) {
                                dadosDepois = null;
                            }
                        }

                        const campos = compararDados(dadosAntes, dadosDepois);
                        setCamposAlterados(campos);
                    } else {
                        setCamposAlterados([]);
                    }
                } else {
                    setCamposAlterados([]);
                }

                if (item.justificativa) {
                    setJustificativa(item.justificativa);
                }
            } catch (error: any) {
                setCamposAlterados([]);
                setJustificativa(null);
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id, item.justificativa]);

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const semDados = camposAlterados.length === 0 && !justificativa;

    if (semDados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            {camposAlterados.length > 0 && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Principal</h4>
                        <TableContainer>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="field-col">Campo</th>
                                        <th className="before-col">Antes</th>
                                        <th className="after-col">Depois</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {camposAlterados.map((campo, index) => (
                                        <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                            <td className="field-cell">{getFieldLabel(campo.campo)}</td>
                                            <td className="before-cell">{formatValue(campo.antes)}</td>
                                            <td className="after-cell">
                                                <div className="after-content">
                                                    <span className="highlight-bar" />
                                                    <span>{formatValue(campo.depois)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TableContainer>
                    </div>
                </>
            )}

            {justificativa && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Observações da finalização</h4>
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                            borderRadius: '6px',
                            color: 'black',
                            fontSize: '11px',
                            lineHeight: '20px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {justificativa}
                        </div>
                    </div>
                </>
            )}
        </ExpandedContent>
    );
}

